import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsClockwise, X } from "@phosphor-icons/react";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const PERIOD_LABEL = { daily: "day", weekly: "week", monthly: "month", quarterly: "quarter", yearly: "year" };

export const periodText = (t) => {
  const unit = PERIOD_LABEL[t.plan_period] || "month";
  return t.plan_interval > 1 ? `every ${t.plan_interval} ${unit}s` : unit;
};

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export const SubscribeButton = ({ tool, owned }) => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [offer, setOffer] = useState(null);

  const applyCode = async () => {
    if (!user) { navigate("/login"); return; }
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/subscriptions/check-coupon", { tool_id: tool.id, coupon_code: code.trim() });
      setOffer(data);
      toast.success(`${data.code}: ${data.free_days} days free`);
    } catch (err) { setOffer(null); toast.error(extractError(err)); }
  };

  const subscribe = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions/create", { tool_id: tool.id, coupon_code: offer?.code || null });
      if (!(await loadRazorpay())) throw new Error("Failed to load Razorpay");
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "StockTools Pro",
        description: offer
          ? `${offer.free_days} days free · first charge ₹${tool.price_inr} on ${new Date(offer.first_charge_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
          : `${tool.name} · auto-renews each ${periodText(tool)}`,
        prefill: { email: user.email, name: user.name },
        theme: { color: "#00d0ff" },
        handler: async (resp) => {
          try {
            await api.post("/subscriptions/verify", resp);
            toast.success(offer ? `Subscribed! ${offer.free_days} days free, access unlocked.` : "Subscribed! Access unlocked.");
            await refresh();
            navigate("/library");
          } catch (err) { toast.error(extractError(err)); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (err) { toast.error(extractError(err) || err.message); setBusy(false); }
  };

  if (owned) {
    return <Button disabled data-testid={`subscribed-${tool.slug}`} className="w-full rounded-sm font-bold bg-secondary">Subscribed</Button>;
  }
  return (
    <div className="space-y-2">
      {offer ? (
        <div data-testid={`offer-applied-${tool.slug}`} className="text-xs font-mono border border-primary/40 bg-primary/5 rounded-sm p-2 flex items-center justify-between gap-2">
          <span><span className="text-primary">{offer.code}</span> · {offer.free_days} days free · first charge {new Date(offer.first_charge_at).toLocaleDateString()}</span>
          <button onClick={() => { setOffer(null); setCode(""); }} data-testid={`offer-remove-${tool.slug}`} className="text-muted-foreground hover:text-destructive"><X size={12} weight="bold" /></button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Offer code" data-testid={`offer-input-${tool.slug}`} className="rounded-sm font-mono uppercase h-9" />
          <Button type="button" variant="outline" onClick={applyCode} data-testid={`offer-apply-${tool.slug}`} className="rounded-sm h-9">Apply</Button>
        </div>
      )}
    <Button onClick={subscribe} disabled={busy} data-testid={`subscribe-tool-${tool.slug}`} className="w-full rounded-sm font-bold bg-primary text-black hover:bg-primary/90">
      <ArrowsClockwise size={14} weight="bold" className="mr-2" /> {busy ? "Opening..." : offer ? `Start ${offer.free_days}-day free trial` : "Subscribe"}
    </Button>
    {offer && (
      <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid={`offer-note-${tool.slug}`}>
        Today Razorpay only takes a small refundable amount to verify your auto-debit. The first ₹{tool.price_inr} charge happens on {new Date(offer.first_charge_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}.
      </p>
    )}
    </div>
  );
};
